// IndexedDB, kept tiny on purpose. Everything the app must not lose lives here: the device's
// non-extractable keys, the pairing record, the per-peer counters, drafts, Recent and prefs.
// The service worker opens the same database (src/sw/sw.ts), so the schema is shared.

/** Never derive this from the product name: renaming the product must not lose anyone's pairing. */
export const DB_NAME = "whistle-app-v1";
export const DB_VERSION = 1;

export type StoreName = "kv" | "counters" | "drafts" | "recent";

const STORES: Record<StoreName, { keyPath?: string }> = {
  kv: {},
  counters: { keyPath: "peer" },
  drafts: { keyPath: "key" },
  recent: { keyPath: "id" },
};

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const [name, opts] of Object.entries(STORES)) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, opts.keyPath ? { keyPath: opts.keyPath } : undefined);
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error("IndexedDB failed to open"));
    req.onblocked = () => reject(new Error("IndexedDB open blocked"));
  });
  return dbPromise;
}

/** For tests: forget the cached connection so a fresh fake database can be used. */
export function resetDbForTests(): void {
  dbPromise = null;
}

function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
  });
}

export async function dbGet<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDb();
  const tx = db.transaction(store, "readonly");
  return requestToPromise(tx.objectStore(store).get(key)) as Promise<T | undefined>;
}

export async function dbGetAll<T>(store: StoreName): Promise<T[]> {
  const db = await openDb();
  const tx = db.transaction(store, "readonly");
  return requestToPromise(tx.objectStore(store).getAll()) as Promise<T[]>;
}

/** Resolves only once the transaction has committed, so callers can rely on the write being durable. */
export async function dbPut(store: StoreName, value: unknown, key?: IDBValidKey): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  tx.objectStore(store).put(value, key);
  await txDone(tx);
}

export async function dbDelete(store: StoreName, key: IDBValidKey): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  tx.objectStore(store).delete(key);
  await txDone(tx);
}

export async function dbClear(store: StoreName): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  tx.objectStore(store).clear();
  await txDone(tx);
}

/** Deletes every record the predicate picks, in one transaction. Resolves once it has committed. */
export async function dbDeleteWhere<T>(store: StoreName, pick: (value: T) => boolean): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  const req = tx.objectStore(store).openCursor();
  req.onsuccess = () => {
    const cursor = req.result;
    if (!cursor) return;
    if (pick(cursor.value as T)) cursor.delete();
    cursor.continue();
  };
  await txDone(tx);
}

// ---------- Counters (docs/protocol.md, "Envelopes": stored before a message is sent or accepted) ----------

export type CounterRecord = { peer: string; out: number; in: number };

/**
 * Counters are per (sender, recipient, channel): the machine, this phone's device identity, and "ws" for the relay
 * socket or "push" for alert envelopes that arrive through Web Push.
 */
export type CounterPeer = { machineId: string; deviceId: string; channel?: "ws" | "push" };

export const counterKey = ({ machineId, deviceId, channel = "ws" }: CounterPeer) => `${machineId}|${deviceId}${channel === "push" ? "#push" : ""}`;
const legacyKey = ({ machineId, channel = "ws" }: CounterPeer) => (channel === "push" ? `${machineId}#push` : machineId);

/**
 * A peer's counters, inside a readwrite transaction on counters and kv. Records are never deleted (security review 2,
 * S26): pairing again with the same machine and device keys carries on from the high-water marks, so the relay can't
 * replay the earlier pairing's envelopes, and a pairing with a new device identity has records of its own.
 *
 * Before 21 Oct 2026 records were keyed by the machine alone and cleared on unpair, so one under the old key belongs to
 * the pairing saved now. It moves to the new key once; starting from 0 would make the daemon refuse this phone.
 */
async function counterRecord(tx: IDBTransaction, peer: CounterPeer): Promise<CounterRecord> {
  const store = tx.objectStore("counters");
  const key = counterKey(peer);
  const current = (await requestToPromise(store.get(key))) as CounterRecord | undefined;
  if (current) return current;
  const old = (await requestToPromise(store.get(legacyKey(peer)))) as CounterRecord | undefined;
  if (old) {
    const pairing = (await requestToPromise(tx.objectStore("kv").get("pairing"))) as { machineId?: unknown; deviceId?: unknown } | undefined;
    if (pairing?.machineId === peer.machineId && pairing.deviceId === peer.deviceId) {
      const moved: CounterRecord = { peer: key, out: old.out, in: old.in };
      store.delete(legacyKey(peer));
      store.put(moved);
      return moved;
    }
  }
  return { peer: key, out: 0, in: 0 };
}

async function counterTx(): Promise<IDBTransaction> {
  const db = await openDb();
  return db.transaction(["counters", "kv"], "readwrite");
}

/**
 * Reserve the next outgoing counter for a peer. The increment is committed before this resolves,
 * so a crash after sealing can never reuse a counter.
 */
export async function reserveOutCounter(peer: CounterPeer): Promise<number> {
  const tx = await counterTx();
  const current = await counterRecord(tx, peer);
  const next: CounterRecord = { ...current, out: current.out + 1 };
  tx.objectStore("counters").put(next);
  await txDone(tx);
  return next.out;
}

export async function getInCounter(peer: CounterPeer): Promise<number> {
  const tx = await counterTx();
  const rec = await counterRecord(tx, peer);
  await txDone(tx);
  return rec.in;
}

/** Record an accepted incoming counter. Only moves forward; committed before the message is used. */
export async function acceptInCounter(peer: CounterPeer, ctr: number): Promise<void> {
  const tx = await counterTx();
  const current = await counterRecord(tx, peer);
  if (current.in >= ctr) {
    tx.abort();
    throw new Error(`counter ${ctr} already accepted`);
  }
  tx.objectStore("counters").put({ ...current, in: ctr } satisfies CounterRecord);
  await txDone(tx);
}

/** Ask the browser to keep this origin's storage; losing it would lose the pairing (SPEC section 4). */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (typeof navigator === "undefined" || !navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
